'use client';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { isBrowser } from '@federa/utils/browser/detection';
import { createContextHook } from '@federa/components/hooks/createContextHook';
import { notifications as notificationTimings, animations } from '@federa/const/timing-tokens';
import {
	FEEDBACK_VARIANT_COLORS,
	GHOST_BUTTON_BASE,
	MUTED_LABEL_SECONDARY,
	TEXT_SM_MEDIUM_PRIMARY,
} from '@federa/components/common/layout-styles';
import { BORDER_MUTED, FLEX_COLUMN_GAP2_STYLE, TEXT_SM_MEDIUM } from '@federa/components/common/style-primitives';
import { FLEX_ROW_START_STYLE } from '@federa/const/style-flex-presets';
import { SemanticIcon } from '@federa/components/icons/SemanticIcon';
import { generateRandomId } from '@federa/utils/crypto/uuid';
import { Button } from '../primitives/Button.tsx';
import { CloseButton } from '../primitives/CloseButton.tsx';
import { colors } from '../css-refs.colors.ts';
import { spacing } from '../css-refs.spacing.ts';
import { borderRadius, borderWidth, boxShadow, zIndex } from '../css-refs.effects.ts';
import { FEEDBACK_VARIANT_ICON_NAMES } from './feedback-variant-icons.ts';
import toastStyles from './Toast.module.css';
import { feedbackAnnouncementOf } from './feedback-announcement.ts';
import type { IconColor } from '@federa/types/ui-icons';
import type { FeedbackVariant } from '@federa/types/ui-feedback-variant';
import type { ToastProviderProps, ToastContextValue, ToastNotification as Toast } from '@federa/types/ui-toast';

const ToastContext = React.createContext<ToastContextValue | null>(null);
export const useToast = createContextHook(ToastContext, 'useToast', 'ToastProvider');

const VARIANT_DURATIONS: Record<FeedbackVariant, number> = {
	success: notificationTimings.toastSuccess,
	error: notificationTimings.toastError,
	warning: notificationTimings.toastWarning,
	info: notificationTimings.toastInfo,
};
const VARIANT_ICON_COLORS: Record<FeedbackVariant, IconColor> = {
	success: 'success',
	error: 'error',
	warning: 'warning',
	info: 'primary',
};
const headerStyles: React.CSSProperties = {
	...FLEX_ROW_START_STYLE,
	gap: spacing[2],
};
const iconContainerStyles: React.CSSProperties = {
	flexShrink: 0,
	marginTop: spacing[0.5],
};
const contentStyles: React.CSSProperties = { flex: 1 };
const titleStyles: React.CSSProperties = {
	...TEXT_SM_MEDIUM_PRIMARY,
	margin: 0,
};
const descriptionStyles: React.CSSProperties = {
	...MUTED_LABEL_SECONDARY,
	margin: 0,
	marginTop: spacing[1],
};
const actionStyles: React.CSSProperties = {
	marginTop: spacing[2],
	paddingTop: spacing[2],
	borderTop: BORDER_MUTED,
};

const getToastStyles = (variant: FeedbackVariant): React.CSSProperties => ({
	backgroundColor: colors.background.primary,
	// UNIFORM ON ALL FOUR SIDES, matching `Alert`. This was a 4px LEFT-only rule, so the toast had one heavy edge and
	// three bare ones and its corners did not agree with each other. The variant still reads off the border colour and
	// the icon; only the asymmetry is gone (operator directive 2026-08-27).
	border: `${borderWidth.DEFAULT} solid ${FEEDBACK_VARIANT_COLORS[variant]}`,
	borderRadius: borderRadius.md,
	boxShadow: boxShadow.lg,
	padding: spacing[4],
	minWidth: spacing[80],
	maxWidth: spacing[96],
	pointerEvents: 'auto',
});
const getActionButtonStyles = (variant: FeedbackVariant): React.CSSProperties => ({
	...GHOST_BUTTON_BASE,
	color: FEEDBACK_VARIANT_COLORS[variant],
	...TEXT_SM_MEDIUM,
	padding: 0,
});

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
	const [isExiting, setIsExiting] = React.useState(false);
	const handleRemove = React.useCallback(() => {
		setIsExiting(true);
		setTimeout(() => onRemove(toast.id), animations.base);
	}, [toast.id, onRemove]);

	return (
		<div
			style={getToastStyles(toast.variant)}
			className={isExiting ? toastStyles.toastExit : toastStyles.toastEnter}
			role={feedbackAnnouncementOf(toast.variant).role}
			aria-live={feedbackAnnouncementOf(toast.variant).live}
			aria-atomic="true"
		>
			<div
				style={{
					...headerStyles,
					marginBottom: toast.description ? spacing[1] : 0,
				}}
			>
				{toast.showIcon !== false && (
					<div style={iconContainerStyles}>
						<SemanticIcon name={FEEDBACK_VARIANT_ICON_NAMES[toast.variant]} size="md" color={VARIANT_ICON_COLORS[toast.variant]} />
					</div>
				)}
				<div style={contentStyles}>
					<p style={titleStyles}>{toast.title}</p>
					{toast.description && <p style={descriptionStyles}>{toast.description}</p>}
				</div>
				{/* The hand-rolled hover state and its style bag are gone: `CloseButton` owns the dismiss control's repaint,
				    focus ring and touch target, all of which this toast was re-deriving. */}
				<CloseButton onClick={handleRemove} aria-label="Close notification" size="sm" />
			</div>
			{toast.action && (
				<div style={actionStyles}>
					<Button
						variant="ghost"
						size="md"
						onClick={() => {
							toast.action?.onClick();
							handleRemove();
						}}
						style={getActionButtonStyles(toast.variant)}
					>
						{toast.action.label}
					</Button>
				</div>
			)}
		</div>
	);
}

const POSITION_STYLES: Record<NonNullable<ToastProviderProps['position']>, React.CSSProperties> = {
	'top-right': { top: spacing[4], right: spacing[4] },
	'top-left': { top: spacing[4], left: spacing[4] },
	'bottom-right': { bottom: spacing[4], right: spacing[4] },
	'bottom-left': { bottom: spacing[4], left: spacing[4] },
	'top-center': { top: spacing[4], left: '50%', transform: 'translateX(-50%)' },
	'bottom-center': {
		bottom: spacing[4],
		left: '50%',
		transform: 'translateX(-50%)',
	},
};
function ToastContainer({
	toasts,
	onRemove,
	position,
}: {
	toasts: Toast[];
	onRemove: (id: string) => void;
	position: NonNullable<ToastProviderProps['position']>;
}) {
	if (toasts.length === 0) return null;
	const containerStyles: React.CSSProperties = {
		position: 'fixed',
		zIndex: zIndex.toast,
		...FLEX_COLUMN_GAP2_STYLE,
		pointerEvents: 'none',
		...POSITION_STYLES[position],
	};
	const content = (
		<div style={containerStyles}>
			{toasts.map((toast) => (
				<ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
			))}
		</div>
	);
	return isBrowser() ? createPortal(content, document.body) : null;
}

export function ToastProvider({ children, position = 'top-right' }: ToastProviderProps) {
	const [toasts, setToasts] = React.useState<Toast[]>([]);
	const timersRef = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

	const removeToast = React.useCallback((id: string) => {
		const timer = timersRef.current.get(id);
		if (timer) {
			clearTimeout(timer);
			void timersRef.current.delete(id);
		}
		setToasts((prev) => prev.filter((t) => t.id !== id));
	}, []);
	const addToast = React.useCallback(
		(toast: Omit<Toast, 'id'>) => {
			const id = generateRandomId('toast_');
			setToasts((prev) => [...prev, { ...toast, id }]);
			const duration = toast.duration ?? VARIANT_DURATIONS[toast.variant];
			const timer = setTimeout(() => removeToast(id), duration);
			timersRef.current.set(id, timer);
			return id;
		},
		[removeToast],
	);

	React.useEffect(() => {
		const timers = timersRef.current;
		return () => {
			for (const t of timers.values()) clearTimeout(t);
			timers.clear();
		};
	}, []);

	return (
		<ToastContext.Provider value={{ toasts, addToast, removeToast }}>
			{children}
			<ToastContainer toasts={toasts} onRemove={removeToast} position={position} />
		</ToastContext.Provider>
	);
}
